import TwoFactorService from '@/services/twoFactorService'

/**
 * Vuex module for Two-Factor Authentication (2FA) management.
 * Handles state for 2FA setup, verification, and recovery.
 */
export default {
    namespaced: true,
    state: {
        qrCodeSvg: null,
        secret: null,
        recoveryCodes: [],
        showSetupModal: false,
        showVerifyModal: false,
        showRecoveryModal: false,
        tempToken: null,
    },
    mutations: {
        SET_QR_CODE(state, { svg, secret }) {
            state.qrCodeSvg = svg
            state.secret = secret
        },
        SET_RECOVERY_CODES(state, codes) {
            state.recoveryCodes = codes
        },
        SET_TEMP_TOKEN(state, token) {
            state.tempToken = token
        },
        SET_SHOW_SETUP_MODAL(state, val) {
            state.showSetupModal = val
        },
        SET_SHOW_VERIFY_MODAL(state, val) {
            state.showVerifyModal = val
        },
        SET_SHOW_RECOVERY_MODAL(state, val) {
            state.showRecoveryModal = val
        },
        CLEAR_SETUP_DATA(state) {
            state.qrCodeSvg = null
            state.secret = null
        },
        RESET_2FA_STATE(state) {
            state.tempToken = null
            state.showVerifyModal = false
            state.showRecoveryModal = false
        }
    },
    actions: {
        /**
         * Initialize 2FA setup by generating a secret key and QR code.
         * 
         * @param {Object} context - Vuex action context
         * @returns {Promise<Object>} The API response data
         */
        async setup({ commit }) {
            const response = await TwoFactorService.setup()
            const data = response.data
            if (data.status || data.success) {
                commit('SET_QR_CODE', {
                    svg: data.message?.qr_code || data.data?.qr_code_svg,
                    secret: data.message?.secret || data.data?.secret
                })
                return data
            }
        },

        /**
         * Confirm 2FA setup using a verification code from the authenticator app.
         * 
         * @param {Object} context - Vuex action context
         * @param {string} code - The 6-digit verification code
         * @returns {Promise<Object>} The API response data
         */
        async confirm({ commit, dispatch }, code) {
            const response = await TwoFactorService.confirm(code)
            const data = response.data
            if (data.status || data.success) {
                commit('CLEAR_SETUP_DATA')
                const recoveryCodes = data.message?.recovery_codes || data.data?.recovery_codes
                commit('SET_RECOVERY_CODES', recoveryCodes)
                dispatch('Auth/auth_check', null, { root: true })
                return data
            }
        },

        /**
         * Disable 2FA for the current user.
         * 
         * @param {Object} context - Vuex action context
         * @param {string} password - The user's current password for confirmation
         * @returns {Promise<Object>} The API response data
         */
        async disable({ dispatch }, password) {
            const response = await TwoFactorService.disable(password)
            const data = response.data
            if (data.status || data.success) {
                dispatch('Auth/auth_check', null, { root: true })
                return data
            }
        },

        /**
         * Retrieve the unused recovery codes count.
         * 
         * @param {Object} context - Vuex action context
         * @returns {Promise<Object>} The API response data
         */
        async getRecoveryCodes() {
            const response = await TwoFactorService.getRecoveryCodes()
            return response.data
        },

        /**
         * Regenerate recovery codes for the current user.
         * 
         * @param {Object} context - Vuex action context
         * @param {string} password - The user's current password for confirmation
         * @returns {Promise<Object>} The API response data
         */
        async regenerateRecoveryCodes({ commit }, password) {
            const response = await TwoFactorService.regenerateRecoveryCodes(password)
            const data = response.data
            if (data.status || data.success) {
                const recoveryCodes = data.message?.recovery_codes || data.data?.recovery_codes
                commit('SET_RECOVERY_CODES', recoveryCodes)
                return data
            }
        },

        /**
         * Verify 2FA during the login process.
         * 
         * @param {Object} context - Vuex action context
         * @param {string} code - The 6-digit verification code
         * @returns {Promise<Object>} Success status and user data
         */
        async verifyLogin({ state, commit, dispatch }, code) {
            if (!state.tempToken) throw new Error('No temporary token available')

            const response = await TwoFactorService.verify2FA(code, state.tempToken)
            if (response.data.status) {
                const user = response.data.data
                const token = user.token

                const storageData = {
                    token,
                    username: user.name,
                    branch_id: user.branch_id,
                    corporate_id: user.corporate_id,
                    email: user.email,
                    id: user.id,
                    member: user.member,
                    merchant_id: user.merchant_id,
                    level: user.level
                }

                Object.entries(storageData).forEach(([key, value]) => {
                    localStorage.setItem(key, value)
                })

                commit('Auth/auth_success', { token, user }, { root: true })
                commit('SET_TEMP_TOKEN', null)
                commit('SET_SHOW_VERIFY_MODAL', false)

                try {
                    dispatch('Auth/storeBusinessProfile', {
                        data: {
                            corporate_id: user.corporate_id,
                            branch_id: user.branch_id,
                            merchant_id: user.merchant_id
                        }
                    }, { root: true })

                    await dispatch('Auth/read', null, { root: true })
                    await dispatch('Auth/auth_check', null, { root: true })
                } catch (profileError) {
                    console.warn('[2FA] Profile data loading failed', profileError);
                }

                return { success: true, user: response.data.data }
            }
        },

        /**
         * Recover account access using a recovery code during login.
         * 
         * @param {Object} context - Vuex action context
         * @param {string} recoveryCode - One of the user's recovery codes
         * @returns {Promise<Object>} Success status and user data
         */
        async recoverLogin({ state, commit, dispatch }, recoveryCode) {
            if (!state.tempToken) throw new Error('No temporary token available')

            const response = await TwoFactorService.recover2FA(recoveryCode, state.tempToken)
            if (response.data.status) {
                const user = response.data.data
                const token = user.token

                const storageData = {
                    token,
                    username: user.name,
                    branch_id: user.branch_id,
                    corporate_id: user.corporate_id,
                    email: user.email,
                    id: user.id,
                    member: user.member,
                    merchant_id: user.merchant_id,
                    level: user.level
                }

                Object.entries(storageData).forEach(([key, value]) => {
                    localStorage.setItem(key, value)
                })

                commit('Auth/auth_success', { token, user }, { root: true })
                commit('SET_TEMP_TOKEN', null)
                commit('SET_SHOW_RECOVERY_MODAL', false)

                try {
                    dispatch('Auth/storeBusinessProfile', {
                        data: {
                            corporate_id: user.corporate_id,
                            branch_id: user.branch_id,
                            merchant_id: user.merchant_id
                        }
                    }, { root: true })

                    await dispatch('Auth/read', null, { root: true })
                    await dispatch('Auth/auth_check', null, { root: true })
                } catch (profileError) {
                    console.warn('[2FA] Profile data loading fallback failed', profileError);
                }

                return { success: true, user: response.data.data }
            }
        }
    },
    getters: {
        qrCodeSvg: state => state.qrCodeSvg,
        recoveryCodes: state => state.recoveryCodes,
        showSetupModal: state => state.showSetupModal,
        showVerifyModal: state => state.showVerifyModal,
        showRecoveryModal: state => state.showRecoveryModal,
        tempToken: state => state.tempToken
    }
}

